import { apiService } from './api'
import { TWO_FACTOR_ENDPOINTS } from '../../api/endpoints'
import { type User } from '../types'

export interface TwoFactorSetupResponse {
  secret: string
  qrCode: string
  backupCodes: string[]
}

export interface TwoFactorStatusResponse {
  enabled: boolean
  lastUsed: string | null
}

export interface TwoFactorVerifyRequest {
  token: string
  userId?: string
}

export interface TwoFactorEnableRequest {
  token: string
}

export interface TwoFactorDisableRequest {
  password: string
}

export interface TwoFactorEnableResponse {
  backupCodes: string[]
  user?: User  // Full user data returned when using setup token
}

class TwoFactorService {
  /**
   * Setup 2FA - generates secret and QR code
   */
  async setup(): Promise<TwoFactorSetupResponse> {
    try {
      const response = await apiService.post<{ data: TwoFactorSetupResponse }>(
        TWO_FACTOR_ENDPOINTS.SETUP
      )
      return response.data
    } catch (error) {
      console.error('2FA setup failed:', error)
      throw error
    }
  }

  /**
   * Enable 2FA - verify token and enable 2FA for the user.
   * When called with a setup token, also returns user data and sets full access cookie.
   */
  async enable(token: string): Promise<TwoFactorEnableResponse> {
    try {
      const response = await apiService.post<{ data: TwoFactorEnableResponse }>(
        TWO_FACTOR_ENDPOINTS.ENABLE,
        { token }
      )
      return response.data
    } catch (error) {
      console.error('2FA enable failed:', error)
      throw error
    }
  }

  /**
   * Verify 2FA token (for login or testing)
   */
  async verify(token: string, userId?: string): Promise<void> {
    try {
      const payload: TwoFactorVerifyRequest = { token }
      if (userId) {
        payload.userId = userId
      }
      await apiService.post(TWO_FACTOR_ENDPOINTS.VERIFY, payload)
    } catch (error) {
      console.error('2FA verification failed:', error)
      throw error
    }
  }

  /**
   * Get 2FA status for the current user
   */
  async getStatus(): Promise<TwoFactorStatusResponse> {
    try {
      const response = await apiService.get<{ data: TwoFactorStatusResponse }>(
        TWO_FACTOR_ENDPOINTS.STATUS
      )
      return response.data
    } catch (error) {
      console.error('Failed to get 2FA status:', error)
      throw error
    }
  }

  /**
   * Regenerate backup codes - requires password for security
   */
  async regenerateBackupCodes(password: string): Promise<{ backupCodes: string[] }> {
    try {
      const response = await apiService.post<{ data: { backupCodes: string[] } }>(
        TWO_FACTOR_ENDPOINTS.REGENERATE_BACKUP_CODES,
        { password }
      )
      return response.data
    } catch (error) {
      console.error('Failed to regenerate backup codes:', error)
      throw error
    }
  }
}

export const twoFactorService = new TwoFactorService()
